import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Dropdown, message } from "antd";
import {
  fetchPopularFields,
  fetchSalesforceFields,
  fetchTemplateFile,
  fetchTemplateLayout,
  fetchTemplateMappings,
  fetchTemplatePageImage,
  fetchTemplateSignatureFields,
  fetchTransformationChoices,
  removeTemplateFields,
  saveTemplateFile,
  saveTemplateMappings,
  saveTemplateSignatureFields,
} from "../../../../service/settings";
import useAuth from "../../../../hooks/useAuth";
import FieldMappingModal, { SOURCE_TO_OBJECT, humanize, suggestMapping } from "../FieldMappingModal";
import StudioCanvas from "./StudioCanvas";
import StudioSidebar, { FieldsTab, SignaturesTab } from "./StudioSidebar";
import WordDraftPanel from "./WordDraftPanel";
import {
  attachGeometry,
  boxAtPoint,
  buildMappingPayload,
  mappingsFromFields,
  newBoxId,
  savedFieldsToStudioBoxes,
  signersSummary,
  studioBoxesToSavedFields,
} from "./templateStudioModel";

const RENDER_SCALE = 2;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f8fafc;
  font-family: "Manrope", sans-serif;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 18px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  min-height: 58px;
`;

const HeaderGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 48vw;
`;

const Pill = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  text-transform: uppercase;
  background: ${(p) => p.$bg || "#f1f5f9"};
  color: ${(p) => p.$fg || "#475569"};
  border: 1px solid ${(p) => p.$border || "#e2e8f0"};
  white-space: nowrap;
`;

const Button = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0f172a;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #f1f5f9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  &:hover {
    background: #1d4ed8;
  }
`;

const Status = styled.span`
  font-size: 13px;
  color: ${(p) => (p.$error ? "#b91c1c" : "#475569")};
`;

const Main = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const CanvasColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ZoomBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  color: #475569;
`;

const Legend = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 14px;
  &::before {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 2px;
    border: 2px solid ${(p) => p.$color};
    background: ${(p) => p.$color}22;
  }
`;

const Centered = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #475569;
  font-size: 14px;
  padding: 40px;
  text-align: center;
`;

const HiddenInput = styled.input`
  display: none;
`;

const TemplateStudio = () => {
  const { formstackId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasCapability } = useAuth();
  const canEditBoxes = hasCapability("esign.manage_forms");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [layout, setLayout] = useState(null);
  const [images, setImages] = useState({});
  const [fields, setFields] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [mappings, setMappings] = useState({});
  const [savedMappingsJson, setSavedMappingsJson] = useState("{}");
  const [passthrough, setPassthrough] = useState({ conditionalMappings: [], mergeFieldMappings: [] });
  const [dataSources, setDataSources] = useState([]);
  const [transformations, setTransformations] = useState([]);
  const [sfFieldsCache, setSfFieldsCache] = useState({});
  const [popularFieldsByObject, setPopularFieldsByObject] = useState({});
  const [boxes, setBoxes] = useState([]);
  const [savedBoxesJson, setSavedBoxesJson] = useState("[]");
  const [otherSignatureFields, setOtherSignatureFields] = useState([]);
  const [selectedFieldName, setSelectedFieldName] = useState(null);
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [focus, setFocus] = useState(null);
  const requestFocus = useCallback((key) => setFocus({ key, nonce: Date.now() + Math.random() }), []);
  const [tab, setTab] = useState("fields");
  const [placement, setPlacement] = useState(null);
  const [showHints, setShowHints] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [fitScale, setFitScale] = useState(1);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingField, setRemovingField] = useState(false);
  const [advancedTab, setAdvancedTab] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const canvasColumnRef = useRef(null);
  const uploadInputRef = useRef(null);
  const imageUrlsRef = useRef([]);

  const templateName = layout?.template_name || searchParams.get("name") || `Template ${formstackId}`;
  const templateType = layout?.template_type || (searchParams.get("type") || "pdf").toLowerCase();
  const isPdf = templateType === "pdf";

  const mappingsDirty = JSON.stringify(mappings) !== savedMappingsJson;
  const boxesDirty = JSON.stringify(boxes) !== savedBoxesJson;
  const dirty = mappingsDirty || boxesDirty;

  // ---- Load everything ----
  useEffect(() => {
    let cancelled = false;
    const revokeImages = () => {
      imageUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      imageUrlsRef.current = [];
    };

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      setImages({});
      revokeImages();
      try {
        const [layoutRes, mappingRes, choicesRes, popularRes, sigRes] = await Promise.all([
          fetchTemplateLayout(formstackId),
          fetchTemplateMappings(formstackId),
          fetchTransformationChoices(),
          fetchPopularFields().catch(() => null),
          fetchTemplateSignatureFields(formstackId).catch(() => null),
        ]);
        if (cancelled) return;

        const layoutData = layoutRes?.data || {};
        const mappingData = mappingRes?.data || {};
        const choices = choicesRes?.data || {};

        setLayout(layoutData);
        const { fields: placed, orphans: unmatched } = attachGeometry(mappingData.fields || [], layoutData.fields || []);
        setFields(placed);
        setOrphans(unmatched);
        const initialMappings = mappingsFromFields(mappingData.fields || []);
        setMappings(initialMappings);
        setSavedMappingsJson(JSON.stringify(initialMappings));
        setPassthrough({
          conditionalMappings: mappingData.conditional_mappings || [],
          mergeFieldMappings: mappingData.merge_field_mappings || [],
        });
        setDataSources(choices.data_sources || []);
        setTransformations(choices.transformations || []);

        if (popularRes) {
          const byObj = popularRes?.data?.popular_fields || {};
          setPopularFieldsByObject(
            Object.fromEntries(Object.entries(byObj).map(([obj, items]) => [obj, items.map((i) => i.name)]))
          );
        }

        const savedSignatureFields = sigRes?.data?.signature_fields || [];
        const studioBoxes = savedFieldsToStudioBoxes(savedSignatureFields, layoutData.pages || []);
        setBoxes(studioBoxes);
        setSavedBoxesJson(JSON.stringify(studioBoxes));
        setOtherSignatureFields(savedSignatureFields.filter((f) => f.field_type === "stamped_image"));

        const needed = new Set(
          Object.values(initialMappings)
            .map((m) => SOURCE_TO_OBJECT[m.sf_object])
            .filter(Boolean)
        );
        needed.forEach((objectType) => {
          fetchSalesforceFields(objectType)
            .then((res) => {
              if (cancelled) return;
              setSfFieldsCache((prev) => ({ ...prev, [objectType]: res?.data?.fields || [] }));
            })
            .catch(() => {});
        });

        setLoading(false);

        // Page images arrive after the layout so the outlines can show immediately.
        for (const page of layoutData.pages || []) {
          if (cancelled) return;
          try {
            // eslint-disable-next-line no-await-in-loop
            const res = await fetchTemplatePageImage(formstackId, page.page_number, RENDER_SCALE);
            if (cancelled) return;
            const url = URL.createObjectURL(res.data);
            imageUrlsRef.current.push(url);
            setImages((prev) => ({ ...prev, [page.page_number]: url }));
          } catch (err) {
            console.error(`Could not render page ${page.page_number}`, err);
          }
        }
      } catch (err) {
        console.error("Failed to load template", err);
        if (!cancelled) {
          setLoadError(err?.response?.data?.error || "This template could not be loaded.");
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      revokeImages();
    };
  }, [formstackId, reloadToken]);

  // ---- Fit-to-width zoom (layout effect so the first paint is already fitted) ----
  useLayoutEffect(() => {
    const element = canvasColumnRef.current;
    if (!element || !layout?.pages?.length) return undefined;
    const widest = Math.max(...layout.pages.map((p) => p.width));
    const compute = () => setFitScale(Math.max(0.3, (element.clientWidth - 48) / widest));
    compute();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(compute);
    observer.observe(element);
    return () => observer.disconnect();
  }, [layout]);

  const scale = fitScale * zoom;

  // ---- Warn before leaving with unsaved work ----
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // ---- Mapping edits ----
  const loadSfFields = useCallback(
    async (objectType, { refresh } = {}) => {
      if (!objectType) return [];
      if (!refresh && sfFieldsCache[objectType]) return sfFieldsCache[objectType];
      const res = await fetchSalesforceFields(objectType, { refresh });
      const list = res?.data?.fields || [];
      setSfFieldsCache((prev) => ({ ...prev, [objectType]: list }));
      return list;
    },
    [sfFieldsCache]
  );

  const updateMapping = useCallback(
    (fieldName, key, value) => {
      setMappings((prev) => {
        const existing = prev[fieldName] || { sf_object: "", sf_field_name: "", transformation: "", logic_rules: {} };
        const updated = { ...existing, [key]: value };
        if (key === "sf_object") {
          updated.sf_field_name = "";
          if (value) loadSfFields(SOURCE_TO_OBJECT[value]).catch(() => {});
        }
        if (!updated.sf_object && !updated.sf_field_name && !updated.transformation) {
          const next = { ...prev };
          delete next[fieldName];
          return next;
        }
        return { ...prev, [fieldName]: updated };
      });
    },
    [loadSfFields]
  );

  const clearMapping = useCallback((fieldName) => {
    setMappings((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const suggestForField = useCallback(
    async (fieldName) => {
      setSuggesting(true);
      try {
        const types = [...new Set(Object.values(SOURCE_TO_OBJECT))];
        const lists = await Promise.all(types.map((t) => loadSfFields(t).catch(() => [])));
        const byObject = Object.fromEntries(types.map((t, i) => [t, lists[i]]));
        const result = suggestMapping(fieldName, byObject);
        if (result) {
          setMappings((prev) => ({ ...prev, [fieldName]: { ...(prev[fieldName] || {}), ...result } }));
          message.success("Suggested a match — check it and save.");
        } else {
          message.info("No obvious match for that field name.");
        }
      } finally {
        setSuggesting(false);
      }
    },
    [loadSfFields]
  );

  // ---- Selection ----
  const selectField = useCallback((name) => {
    setSelectedFieldName(name);
    setSelectedBoxId(null);
    if (name) {
      setTab("fields");
      requestFocus(`field:${name}`);
    }
  }, [requestFocus]);

  const selectBox = useCallback((id) => {
    setSelectedBoxId(id);
    setSelectedFieldName(null);
    if (id) {
      setTab("signatures");
      requestFocus(`box:${id}`);
    }
  }, [requestFocus]);

  // ---- Signature boxes ----
  const placeBox = useCallback(
    (page, point) => {
      if (!placement || !canEditBoxes) return;
      const box = boxAtPoint(point, page, placement);
      setBoxes((prev) => [...prev, box]);
      setSelectedBoxId(box.id);
      setSelectedFieldName(null);
    },
    [placement, canEditBoxes]
  );

  const updateBox = useCallback((id, patch) => {
    setBoxes((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }, []);

  const deleteBox = useCallback((id) => {
    setBoxes((prev) => prev.filter((b) => b.id !== id));
    setSelectedBoxId((current) => (current === id ? null : current));
  }, []);

  const applySuggestions = useCallback(() => {
    const suggestions = layout?.suggested_boxes || [];
    if (!suggestions.length) return;
    setBoxes((prev) => {
      const overlaps = (a, b) =>
        a.page_number === b.page_number &&
        a.x < b.x + b.width &&
        a.x + a.width > b.x &&
        a.y < b.y + b.height &&
        a.y + a.height > b.y;
      const added = suggestions
        .filter((s) => !prev.some((b) => overlaps(s, b)))
        .map((s) => ({
          id: newBoxId(),
          role: s.role,
          field_type: s.field_type,
          label: s.label || "",
          page_number: s.page_number,
          x: s.x,
          y: s.y,
          width: s.width,
          height: s.height,
        }));
      if (!added.length) {
        message.info("Every suggested spot already has a box.");
        return prev;
      }
      message.success(`Added ${added.length} suggested spot${added.length === 1 ? "" : "s"} — check each one, then save.`);
      return [...prev, ...added];
    });
    setShowHints(true);
    setTab("signatures");
  }, [layout]);

  useEffect(() => {
    if (!placement) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setPlacement(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [placement]);

  // ---- Save ----
  // Resolves to true when everything was saved, false when a save failed.
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      if (mappingsDirty) {
        await saveTemplateMappings(formstackId, buildMappingPayload(mappings, passthrough));
        setSavedMappingsJson(JSON.stringify(mappings));
      }
      if (boxesDirty) {
        const pages = layout?.pages || [];
        const res = await saveTemplateSignatureFields(formstackId, studioBoxesToSavedFields(boxes, pages), { scope: "esign" });
        const saved = savedFieldsToStudioBoxes(res?.data?.signature_fields || [], pages);
        setBoxes(saved);
        setSavedBoxesJson(JSON.stringify(saved));
        setSelectedBoxId(null);
      }
      message.success(
        boxesDirty && boxes.length
          ? `Saved. Documents made from this template are ready to sign (${signersSummary(boxes)}).`
          : "Saved."
      );
      return true;
    } catch (err) {
      console.error("Save failed", err);
      message.error(
        err?.response?.data?.error ||
          (Array.isArray(err?.response?.data?.errors) && err.response.data.errors.join("; ")) ||
          "Save failed — try again."
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [boxes, boxesDirty, formstackId, layout, mappings, mappingsDirty, passthrough]);

  const confirmDiscard = useCallback(
    () => !dirty || window.confirm("You have unsaved changes. Leave without saving?"),
    [dirty]
  );

  const handleBack = useCallback(() => {
    if (!confirmDiscard()) return;
    navigate("/settings");
  }, [confirmDiscard, navigate]);

  // The advanced tools work on (and reload) the saved version, so any unsaved
  // change — fields or signature spots — must be saved first or we'd lose it.
  const openAdvanced = useCallback(
    (which) => {
      if (dirty) {
        const ok = window.confirm("Save your changes first? The advanced tools work on the saved version.");
        if (!ok) return;
        handleSave().then((saved) => {
          if (saved) setAdvancedTab(which);
        });
        return;
      }
      setAdvancedTab(which);
    },
    [dirty, handleSave]
  );

  const handleDownload = useCallback(async () => {
    try {
      const res = await fetchTemplateFile(formstackId);
      const ext = (res.headers?.["x-template-type"] || templateType || "pdf").toLowerCase();
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${templateName}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      message.error("Could not download the template file.");
    }
  }, [formstackId, templateName, templateType]);

  // Removing a fillable box rewrites the template file and reloads the editor,
  // so unsaved work has to be saved first — the same rule as the advanced tools.
  const handleRemoveField = useCallback(
    async (fieldName) => {
      if (removingField) return;
      if (dirty) {
        const ok = window.confirm("Save your changes first? Removing a box reloads the template.");
        if (!ok) return;
      }
      // Busy from here on, including during the save-first step, so nothing
      // else can start a second rewrite of the Formstack file meanwhile.
      setRemovingField(true);
      try {
        if (dirty) {
          const saved = await handleSave();
          if (!saved) return;
        }
        const res = await removeTemplateFields(formstackId, [fieldName]);
        const removed = res?.data?.removed || [];
        message.success(
          removed.length
            ? `Removed "${humanize(fieldName)}" from the PDF. Reloading the template…`
            : "Nothing was removed."
        );
        setSelectedFieldName(null);
        setReloadToken((t) => t + 1);
      } catch (err) {
        message.error(err?.response?.data?.error || "Could not remove that box — try again.");
      } finally {
        setRemovingField(false);
      }
    },
    [dirty, formstackId, handleSave, removingField]
  );

  const handleUpload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext !== templateType) {
        message.error(`Please upload a .${templateType} file to replace this template.`);
        return;
      }
      setUploading(true);
      try {
        await saveTemplateFile(formstackId, file);
        message.success("New file saved. Reloading the template…");
        setReloadToken((t) => t + 1);
      } catch (err) {
        message.error(err?.response?.data?.error || "Upload failed — try again.");
      } finally {
        setUploading(false);
      }
    },
    [formstackId, templateType]
  );

  const moreItems = useMemo(
    () => [
      { key: "rules", label: "Rules & logic" },
      { key: "conditionals", label: "Conditional sections" },
      { key: "preview", label: "Test with a real matter" },
      { type: "divider" },
      { key: "download", label: "Download the file" },
      { key: "upload", label: uploading ? "Uploading…" : "Replace the file", disabled: uploading },
      ...(isPdf
        ? [{ type: "divider" }, { key: "pdf-editor", label: "Edit the PDF's content (opens the PDF editor)" }]
        : []),
    ],
    [isPdf, uploading]
  );

  const onMore = useCallback(
    ({ key }) => {
      if (key === "rules" || key === "conditionals" || key === "preview") openAdvanced(key);
      else if (key === "download") handleDownload();
      else if (key === "upload") {
        // Replacing the file reloads the whole editor, which would drop unsaved work.
        if (!dirty || window.confirm("You have unsaved changes. Replacing the file will discard them. Continue?")) {
          uploadInputRef.current?.click();
        }
      } else if (key === "pdf-editor") {
        if (!confirmDiscard()) return;
        const params = new URLSearchParams({ name: templateName, type: templateType });
        window.open(`/template-editor/${formstackId}?${params}`, "_blank", "noopener,noreferrer");
      }
    },
    [confirmDiscard, dirty, formstackId, handleDownload, openAdvanced, templateName, templateType]
  );

  const signable = isPdf ? boxes.length > 0 || layout?.signature?.ready : Boolean(layout?.signature?.ready);
  const signers = isPdf && boxes.length ? signersSummary(boxes) : (layout?.signature?.signers || []).join(" + ");

  return (
    <Page>
      <Header>
        <HeaderGroup>
          <Button type="button" onClick={handleBack} data-testid="studio-back">
            ← File templates
          </Button>
          <Title title={templateName}>{templateName}</Title>
          <Pill>{templateType}</Pill>
          {!loading && !loadError && (
            <Pill
              data-testid="studio-signable-pill"
              $bg={signable ? "#ede9fe" : "#f3f4f6"}
              $fg={signable ? "#5b21b6" : "#6b7280"}
              $border={signable ? "#ddd6fe" : "#e5e7eb"}
            >
              {signable ? `Signable${signers ? ` · ${signers}` : ""}` : "No signature spots"}
            </Pill>
          )}
        </HeaderGroup>
        <HeaderGroup>
          {dirty && !saving && <Status data-testid="studio-dirty">Unsaved changes</Status>}
          <Dropdown menu={{ items: moreItems, onClick: onMore }} trigger={["click"]} disabled={loading || Boolean(loadError)}>
            <Button type="button" data-testid="studio-more">
              More ▾
            </Button>
          </Dropdown>
          <HiddenInput
            ref={uploadInputRef}
            type="file"
            accept={isPdf ? ".pdf,application/pdf" : ".docx"}
            onChange={handleUpload}
            data-testid="studio-upload-input"
          />
          <PrimaryButton type="button" onClick={handleSave} disabled={!dirty || saving || loading} data-testid="studio-save">
            {saving ? "Saving…" : "Save"}
          </PrimaryButton>
        </HeaderGroup>
      </Header>

      {loadError ? (
        <Centered data-testid="studio-error">
          <strong>{loadError}</strong>
          <Button type="button" onClick={() => setReloadToken((t) => t + 1)}>
            Try again
          </Button>
        </Centered>
      ) : (
        <Main>
          <CanvasColumn ref={canvasColumnRef}>
            {isPdf && (
              <ZoomBar>
                <Button type="button" onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))} style={{ padding: "3px 9px" }}>
                  −
                </Button>
                <span style={{ minWidth: 44, textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                <Button type="button" onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100))} style={{ padding: "3px 9px" }}>
                  +
                </Button>
                <Button type="button" onClick={() => setZoom(1)} style={{ padding: "3px 9px" }}>
                  Fit
                </Button>
                <Legend $color="#16a34a">Mapped</Legend>
                <Legend $color="#d97706">Not mapped</Legend>
                <Legend $color="#7c3aed">Signature spot</Legend>
                <Legend $color="#64748b">Checkbox</Legend>
              </ZoomBar>
            )}
            {loading ? (
              <Centered>Loading the template…</Centered>
            ) : isPdf ? (
              <StudioCanvas
                pages={layout?.pages || []}
                images={images}
                scale={scale}
                fields={fields}
                orphans={orphans}
                mappings={mappings}
                selectedFieldName={selectedFieldName}
                onSelectField={selectField}
                boxes={boxes}
                selectedBoxId={selectedBoxId}
                onSelectBox={selectBox}
                onChangeBox={(id, geometry) => updateBox(id, geometry)}
                canEditBoxes={canEditBoxes}
                placement={placement}
                onPlace={(page, point) => placeBox(page, point)}
                showHints={showHints}
                hints={layout?.signature_hints || []}
                focus={focus}
              />
            ) : (
              <WordDraftPanel
                formstackId={formstackId}
                templateName={templateName}
                beforePublish={() =>
                  !dirty ||
                  window.confirm(
                    "You have unsaved field changes here. Publishing reloads the editor and discards them — save first, or continue?"
                  )
                }
                onPublished={() => setReloadToken((t) => t + 1)}
              />
            )}
          </CanvasColumn>

          <StudioSidebar tab={tab} onTabChange={setTab} fieldsCount={fields.length} boxesCount={isPdf ? boxes.length : 0}>
            {loading ? null : tab === "fields" ? (
              <FieldsTab
                fields={fields}
                mappings={mappings}
                templateType={templateType}
                selectedFieldName={selectedFieldName}
                onSelectField={selectField}
                onShowField={(name) => requestFocus(`field:${name}`)}
                onUpdateMapping={updateMapping}
                onClearMapping={clearMapping}
                onRemoveField={isPdf ? handleRemoveField : undefined}
                removingField={removingField}
                onSuggest={suggestForField}
                suggesting={suggesting}
                dataSources={dataSources}
                transformations={transformations}
                sfFieldsCache={sfFieldsCache}
                popularFieldsByObject={popularFieldsByObject}
              />
            ) : (
              <SignaturesTab
                templateType={templateType}
                boxes={boxes}
                selectedBoxId={selectedBoxId}
                onSelectBox={selectBox}
                onShowBox={(id) => requestFocus(`box:${id}`)}
                onUpdateBox={updateBox}
                onDeleteBox={deleteBox}
                placement={placement}
                onArmPlacement={setPlacement}
                onCancelPlacement={() => setPlacement(null)}
                canEdit={canEditBoxes}
                suggestedBoxes={layout?.suggested_boxes || []}
                onApplySuggestions={applySuggestions}
                showHints={showHints}
                onToggleHints={() => setShowHints((v) => !v)}
                signatureSource={layout?.signature?.source || ""}
                poaStampCount={otherSignatureFields.length}
              />
            )}
          </StudioSidebar>
        </Main>
      )}

      <FieldMappingModal
        open={Boolean(advancedTab)}
        initialTab={advancedTab || "fields"}
        template={{ formstack_id: formstackId, name: templateName, template_type: templateType }}
        onClose={() => setAdvancedTab(null)}
        onSaved={() => setReloadToken((t) => t + 1)}
      />
    </Page>
  );
};

export default TemplateStudio;
